import { useState } from 'react';
import Modal from '@/common/Modal';
import Checkbox from '@/common/Checkbox';
import EditableTile from '@/common/EditableTile';
import EditableDropdown from '@/common/EditableDropdown';
import { Nationality } from '@/api/models';
import { Address } from '@/api/models/Address';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import styles from './Modal.module.scss';

export interface IAddressData {
  code: string;
  country: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  isDefault: boolean;
  isActive: boolean;
}

interface IAddressModalProps {
  isOpen: boolean;
  playerName: string;
  addressCodes: string[] | undefined;
  mode: 'create' | 'edit' | undefined;
  address: Address | undefined;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSave: (address: IAddressData) => void;
  nationalities: Nationality[];
}

function AddressModal({ isOpen, onClose, onSave, playerName = '', addressCodes, mode, address, nationalities }: IAddressModalProps) {
  const [code, setCode] = useState(address?.code || '');
  const [country, setCountry] = useState(address?.country || '');
  const [address1, setAddress1] = useState(address?.address1 || '');
  const [address2, setAddress2] = useState(address?.address2 || '');
  const [city, setCity] = useState(address?.city || '');
  const [state, setState] = useState(address?.state || '');
  const [postalCode, setPostalCode] = useState(address?.postalCode || '');
  const [isDefault, setIsDefault] = useState(address?.isDefault || false);
  const [showLabelError, setShowLabelError] = useState(false);
  const isSaveDisabled = !code.trim() || !address1.trim() || !city.trim() || !state.trim() || !postalCode.trim();

  const resetForm = () => {
    setCode('');
    setCountry('United States');
    setAddress1('');
    setAddress2('');
    setCity('');
    setState('');
    setPostalCode('');
    setIsDefault(false);
    setShowLabelError(false);
  };

  const handleSave = (isActive = true) => {
    const codeAlreadyExists = addressCodes?.includes(code.trim().toLowerCase());
    let shouldCheckCode = true;
    if (mode === 'edit') shouldCheckCode = address?.code?.trim().toLowerCase() !== code.trim().toLowerCase(); // has original code changed
    if (shouldCheckCode && codeAlreadyExists) {
      setShowLabelError(true);
      return;
    }
    setShowLabelError(false);
    onSave({
      code: code.trim(),
      country,
      address1: address1.trim(),
      address2: address2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      isDefault,
      isActive,
    });

    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <div data-testid="player-address-modal-wrapper">
      <Modal isOpen={isOpen} onClose={handleCancel}>
        <div className={styles.addressContainer}>
          <div className={styles.titleWrapper}>
            <div className={styles.title}>{mode === 'create' ? 'Add New' : 'Edit'} Address</div>
            <button type="button" className={styles.iconButton} aria-label="Close address modal" onClick={handleCancel}>
              <CloseIcon aria-hidden="true" className={styles.icon} />
            </button>
          </div>

          {playerName && <div className={styles.playerName}>{playerName}</div>}

          <EditableTile label="Label" value={code} onChange={setCode} alignment="left" className={styles.tile} maxLength={50} />

          <EditableDropdown
            label="Country / Territory"
            options={nationalities.map(({ country }) => ({ id: country, label: country }))}
            initialId={country}
            onChange={setCountry}
            alignment="left"
            variant="large"
            className={`${styles.tile} ${styles.dropdown}`}
          />

          <EditableTile label="Street Address 1" value={address1} onChange={setAddress1} alignment="left" className={styles.tile} maxLength={50} />

          <EditableTile label="Street Address 2" value={address2} onChange={setAddress2} alignment="left" className={styles.tile} maxLength={50} />

          <div className={styles.addressRow}>
            <EditableTile label="City" value={city} onChange={setCity} alignment="left" className={styles.halfTile} />

            <EditableTile label="State" value={state} onChange={setState} alignment="left" className={styles.quarterTile} />

            <EditableTile label="Zip" value={postalCode} onChange={setPostalCode} alignment="left" className={styles.quarterTile} />
          </div>

          <Checkbox label="Default Address" checked={isDefault} onClick={() => setIsDefault(!isDefault)} className={styles.checkbox} />

          {showLabelError && <span className={styles.error}>The address label already exists, please enter a different one.</span>}

          <div className={styles.buttonContainer}>
            <button onClick={() => handleSave(false)} className={`${styles.saveButton} ${styles.deactivateButton}`} disabled={isSaveDisabled}>
              Deactivate
            </button>
            <button onClick={handleCancel} className={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={() => handleSave()} className={styles.saveButton} disabled={isSaveDisabled}>
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AddressModal;
